// Data Access Layer: User Profile
// NOTE: Adapt getCurrentUserId() for your auth provider (Appwrite, Auth0, etc.)
import { supabase } from '../supabase';
import type { UserProfile, UserProfileInsert, UserProfileUpdate } from '../../types/database';

/**
 * Get the current authenticated user ID from your auth system
 * ADAPT THIS FOR YOUR AUTH PROVIDER
 */
async function getCurrentUserId(): Promise<string | null> {
  // For Supabase (default):
  const { data: { user } } = await supabase.auth.getUser();
  if (user) return user.id;
  
  // For Appwrite:
  // import { Account } from 'appwrite';
  // const account = new Account(client);
  // const user = await account.get();
  // return user.$id;
  
  // For Auth0 or JWT:
  // Extract user ID from JWT token in cookies/localStorage
  // const token = localStorage.getItem('auth_token');
  // const decoded = jwt.decode(token);
  // return decoded.sub;
  
  return null;
}

/**
 * Fetch the current user's profile
 */
export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    return null;
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user profile:', error);
    throw new Error(`Failed to fetch user profile: ${error.message}`);
  }

  return data;
}

/**
 * Update the current user's profile
 */
export async function updateUserProfile(updates: UserProfileUpdate): Promise<UserProfile> {
  const userId = await getCurrentUserId();
  
  if (!userId) {
    throw new Error('No authenticated user');
  }

  const { data, error } = await supabase
    .from('user_profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating user profile:', error);
    throw new Error(`Failed to update user profile: ${error.message}`);
  }

  return data;
}

/**
 * Create a user profile (usually handled by trigger, but useful for manual creation)
 */
export async function createUserProfile(profile: UserProfileInsert): Promise<UserProfile> {
  const { data, error } = await supabase
    .from('user_profiles')
    .insert(profile)
    .select()
    .single();

  if (error) {
    console.error('Error creating user profile:', error);
    throw new Error(`Failed to create user profile: ${error.message}`);
  }

  return data;
}
